import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mainApi } from '@/lib/api';
import { QUERY_KEYS, StoreType } from '@/lib/constants';
import { stanNotify } from '@/lib/stanNotify';
import { handleApiError, parseApiError } from '@/lib/utils/apiErrorHandler';
import { PageStatus } from '@/types/store';
import { type FunnelTemplateId, persistFunnelTemplate } from '../constants/funnelTemplates';
import type { CreateFunnelResponse, UpdateFunnelPayload } from '../types';

/**
 * Funnel-level (store-level) mutations — create, rename, delete, duplicate.
 * Page-level mutations live in `useFunnelPageMutations.ts`, theme in
 * `useFunnelThemeMutations.ts`.
 */

interface CreateFunnelArgs {
    name?: string;
    template?: FunnelTemplateId;
}

export function useCreateFunnel() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ name, template }: CreateFunnelArgs = {}) => {
            const payload: Record<string, unknown> = { type: 'funnel' };
            if (name) payload.name = name;
            const response = await mainApi.put<CreateFunnelResponse>('v1/stores', payload);
            const data = response.data;
            if (data?.store_id && template) persistFunnelTemplate(data.store_id, template);
            return data;
        },

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA, StoreType.FUNNEL] });
        },
        onError: () => {
            stanNotify({
                type: 'error',
                title: 'Oops!',
                text: 'Something went wrong. Please try again later!',
                duration: 2000,
            });
        },
    });
}

export function useUpdateFunnelDetails() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: UpdateFunnelPayload) => {
            const response = await mainApi.put('v1/stores', payload);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA, StoreType.FUNNEL] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FUNNELS] });
        },
        onError: (error: Error) => {
            const parsed = parseApiError(error);
            if (parsed.status === 409) {
                stanNotify({
                    type: 'error',
                    title: 'Slug already taken',
                    text: 'Please choose a different slug.',
                    duration: 3000,
                });
            } else {
                handleApiError(error, 'Failed to update funnel');
            }
        },
    });
}

export function useDeleteFunnel() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (storeId: number) => {
            await mainApi.delete(`v1/stores/${storeId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA, StoreType.FUNNEL] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FUNNELS] });
            stanNotify({ type: 'success', title: 'Funnel deleted!', duration: 2000 });
        },
        onError: () => {
            stanNotify({
                type: 'error',
                title: 'Oops!',
                text: 'Failed to delete funnel. Please try again.',
                duration: 2000,
            });
        },
    });
}

export function useDuplicateFunnel() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (originalStoreId: number) => {
            const { data: originalData } = await mainApi.get(`v1/stores/${originalStoreId}`, {
                params: { get_details: 1 },
            });
            const original = originalData.store;

            const { data: newStore } = await mainApi.put<CreateFunnelResponse>('v1/stores', {
                name: original.data.name,
                type: 'funnel',
            });

            const originalPages: Record<string, unknown>[] = original.pages || [];

            // Hidden pages (status=DRAFT) round-trip through /pages/draft to stay hidden;
            // everything else publishes normally via /pages.
            const endpointForPage = (status: number | undefined) =>
                status === PageStatus.DRAFT
                    ? `v1/stores/${newStore.store_id}/pages/draft`
                    : `v1/stores/${newStore.store_id}/pages`;

            // Shared payload builder so pass 1 + pass 2 stay in sync (backend's update path
            // re-processes the full product payload, so pass 2 can't just send routing).
            const buildBasePayload = (page: Record<string, unknown>) => {
                const pageData = { ...(page.data as Record<string, unknown>) };
                const productData = { ...(pageData.product as Record<string, unknown>) };
                delete productData.id;

                if (page.type === 'course' && productData.course) {
                    const course = productData.course as Record<string, unknown>;
                    (course.modules as Record<string, unknown>[] | undefined)?.forEach(
                        (mod: Record<string, unknown>) => {
                            delete mod.module_id;
                            (mod.lessons as Record<string, unknown>[] | undefined)?.forEach(
                                (lesson: Record<string, unknown>) => {
                                    delete lesson.lesson_id;
                                }
                            );
                        }
                    );
                    if ('toc' in course) delete course.toc;
                }

                return { pageData, productData };
            };

            // Pass 1 — create each page without routing. V2 routing targets reference the
            // old store's page_ids which don't exist in the new store yet, so the backend
            // would reject them. We remap in pass 2.
            const createdPages = await Promise.all(
                originalPages.map(async (page, index) => {
                    const { pageData, productData } = buildBasePayload(page);
                    const originalStatus = typeof page.status === 'number' ? page.status : undefined;
                    const order = typeof pageData.order === 'number' ? pageData.order : index + 1;

                    const { data: created } = await mainApi.put(endpointForPage(originalStatus), {
                        bottom_title: pageData.bottom_title,
                        top_banner_text: pageData.top_banner_text,
                        product: productData,
                        submit_title: pageData.submit_title,
                        order,
                    });

                    const createdPage = (created?.page ?? created) as Record<string, unknown> | undefined;
                    const newPageId = createdPage?.page_id as number | undefined;

                    return {
                        page,
                        newPageId,
                        originalStatus,
                        originalRouting: pageData.routing as Record<string, unknown> | null | undefined,
                    };
                })
            );

            // Pass 2 — only runs for V2 pages with routing. Rebuild the full payload so the
            // backend's update path doesn't drop product data, then attach remapped routing.
            const idMap = new Map<number, number>();
            createdPages.forEach(({ page, newPageId }) => {
                if (newPageId !== undefined) idMap.set(page.page_id as number, newPageId);
            });

            const routingUpdates = createdPages
                .filter(p => p.newPageId !== undefined && p.originalRouting)
                .map(({ page, newPageId, originalStatus, originalRouting }) => {
                    const { pageData, productData } = buildBasePayload(page);

                    const remap = (target: unknown): Record<string, unknown> | null => {
                        if (!target || typeof target !== 'object') return null;
                        const t = target as Record<string, unknown>;
                        const mapped = idMap.get(t.page_id as number);
                        if (mapped === undefined) return null;
                        return { ...t, page_id: mapped };
                    };

                    const routing = originalRouting as Record<string, unknown>;
                    const newRouting = {
                        on_purchase: remap(routing.on_purchase),
                        on_skip: remap(routing.on_skip),
                    };

                    return mainApi.put(endpointForPage(originalStatus), {
                        page_id: newPageId,
                        type: page.type,
                        bottom_title: pageData.bottom_title,
                        top_banner_text: pageData.top_banner_text,
                        submit_title: pageData.submit_title,
                        product: productData,
                        order: typeof pageData.order === 'number' ? pageData.order : undefined,
                        routing: newRouting,
                    });
                });

            await Promise.all(routingUpdates);
            return newStore;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA, StoreType.FUNNEL] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FUNNELS] });
            stanNotify({ type: 'success', title: 'Funnel duplicated!', duration: 2000 });
        },
        onError: () => {
            stanNotify({
                type: 'error',
                title: 'Oops!',
                text: 'Something went wrong. Please try again later.',
                duration: 2000,
            });
        },
    });
}
